import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AgentHours } from '../lib/billing';
import { progressPct } from '../lib/progress';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';

// Agent-hours band on the Billing screen (arc 101 slice 01). The free tier's
// ceiling is HOURS, and it bites at DISPATCH — a maintainer whose runs stop has
// nowhere else to see why, short of reading logs they don't have. So this is the
// surface that says how much of the window is spent and when it comes back.
//
// Presenter, and deliberately dumb about WHO is capped: it renders whatever
// `GET /v1/billing/usage` sends and nothing when the server sends `null` (an
// uncapped tier, or the arc still dark). Re-deriving the tier rule here would be
// a second copy of `agentHourCapFor` in a browser bundle.
//
// It states the ceiling and its reset, never "your runs are stopped": today the
// gate only OBSERVES (`apps/agents-manager`, `hours_capped` logged, not enforced),
// so a paused-runs claim would be a lie until enforcement flips on.

/** Hours read as a short decimal — `12.5`, not `12.499999999999998`. */
const HOURS = new Intl.NumberFormat('en', { maximumFractionDigits: 1 });

/**
 * Reset day in UTC, matching the window the meter sums over. Formatting the
 * instant in local time would show the day BEFORE for western offsets, and the
 * account's month is a UTC month.
 */
const RESET_DAY = new Intl.DateTimeFormat('en', {
  dateStyle: 'medium',
  timeZone: 'UTC',
});

/** NaN-safe reset date — an unparseable instant renders verbatim (`lib/time.ts` rule). */
const resetDay = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : RESET_DAY.format(d);
};

export function AgentHoursPanel(props: { hours: AgentHours | null | undefined }): JSX.Element {
  // `used >= cap` is the inclusive boundary `assertWithinAgentHourCap` enforces:
  // hit it and the next dispatch is the one that's refused, so the band reads
  // "reached" at exactly the number the gate does.
  const reached = (h: AgentHours): boolean => h.used_hours >= h.cap_hours;

  return (
    <Show when={props.hours}>
      {(h) => (
        <Panel title={t('billing.agent_hours_heading')}>
          <div class="billing-hours__head">
            <span class="billing-hours__label">{t('billing.agent_hours_label')}</span>
            <span class="billing-hours__value">
              {t('billing.agent_hours_progress', {
                used: HOURS.format(h().used_hours),
                cap: HOURS.format(h().cap_hours),
              })}
            </span>
          </div>
          <div
            class="progress billing-hours__gauge"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={h().cap_hours}
            // The REAL number, clamped only so it can't exceed the announced max
            // — the width below is what's clamped for layout.
            aria-valuenow={Math.min(h().used_hours, h().cap_hours)}
            aria-label={t('billing.agent_hours_heading')}
          >
            <div
              class="progress__indicator"
              data-over={reached(h()) ? 'true' : undefined}
              style={{ width: `${progressPct(h().used_hours, h().cap_hours)}%` }}
            />
          </div>
          <div class="billing-hours__foot">
            <span class="billing-hours__reset">
              {t('billing.agent_hours_resets', { date: resetDay(h().resets_at) })}
            </span>
            <Show when={reached(h())}>
              <StatusPill tone="danger">{t('billing.agent_hours_state_reached')}</StatusPill>
            </Show>
          </div>
          <Show when={reached(h())}>
            <p class="billing-hours__note" role="status">
              {t('billing.agent_hours_reached_note', { cap: HOURS.format(h().cap_hours) })}
            </p>
          </Show>
          <p class="billing-hours__desc">{t('billing.agent_hours_desc')}</p>
        </Panel>
      )}
    </Show>
  );
}
